import { OutlineElement } from '../../base/outline-element/outline-element';
import { CSSResultGroup, html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './ghs-location-card.css.lit';

/**
 * The The Location Card component
 *
 * @element ghs-location-card
 */
@customElement('ghs-location-card')
export class GhsLocationCard extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Whether or not the component is small enough to contain the hours.`.
   */
  @state() isSmallClass: String;

  /**
   * Whether or not the component is in the sidebar.
   * If TRUE, some modifier styles will be added.`.
   */
  @property({ type: Boolean, attribute: 'sidebar', reflect: true })
  sidebar = false;

  /**
   * Whether or not the component should be 100% height of its parent.
   * If TRUE, the Host will be `height: 100%`.
   */
  @property({ type: Boolean, attribute: 'height-grow', reflect: true })
  heightGrow = false;

  private resizeObserver: ResizeObserver | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    super.disconnectedCallback();
  }

  handleResize = (entries: ResizeObserverEntry[]): void => {
    for (const entry of entries) {
      const width = entry.contentRect.width;
      this.isSmallClass = width < 620 ? 'is-small' : '';
    }
  };

  render(): TemplateResult {
    const noHours =
      this.querySelector('[slot="location-card-hours"]') === null
        ? 'no-hours'
        : '';

    return html`
      <div
        class="location-card ${this.isSmallClass} ${noHours} ${this.sidebar
          ? 'in-sidebar'
          : ''}"
      >
        <div class="location-card-top-section">
          <div class="location-card-info-section">
            ${this.querySelector('[slot="location-card-headline"]') !== null
              ? html`
                  <div class="location-card-headline ">
                    <slot name="location-card-headline"></slot>
                  </div>
                `
              : nothing}
            <div class="location-card-top-body">
              <div class="location-card-addr-phone">
                ${this.querySelector('[slot="location-card-address"]') !== null
                  ? html`
                      <div class="location-card-address">
                        <slot name="location-card-address"></slot>
                      </div>
                    `
                  : nothing}
                ${this.querySelector('[slot="location-card-phone"]') !== null
                  ? html`
                      <div class="location-card-phone">
                        <slot name="location-card-phone"></slot>
                      </div>
                    `
                  : nothing}
                ${this.querySelector('[slot="location-card-feature"]') !== null
                  ? html`
                      <div class="location-card-feature">
                        <slot name="location-card-feature"></slot>
                      </div>
                    `
                  : nothing}
              </div>
              ${this.querySelector('[slot="location-card-hours"]') !== null
                ? html`
                    <div class="location-card-hours">
                      <slot name="location-card-hours"></slot>
                    </div>
                  `
                : nothing}
              ${this.querySelector('[slot="location-card-image"]') !== null
                ? html`
                    <div class="location-card-image">
                      <slot name="location-card-image"></slot>
                    </div>
                  `
                : nothing}
            </div>
            ${this.querySelector('[slot="location-card-hours"]') !== null
              ? html`
                  <div class="location-card-hours-bottom">
                    <slot name="location-card-hours-bottom"></slot>
                  </div>
                `
              : nothing}
          </div>
        </div>
        <div class="location-card-bottom-section">
          <div class="location-card-cta-wrapper">
            <hr />
            <div class="location-card-cta-section">
              ${this.querySelector('[slot="location-card-location-link"]') !==
              null
                ? html`
                    <div class="location-card-location-link">
                      <slot name="location-card-location-link"></slot>
                    </div>
                  `
                : nothing}
              ${this.querySelector('[slot="location-card-directions-link"]') !==
              null
                ? html`
                    <div class="location-card-directions-link">
                      <slot name="location-card-directions-link"></slot>
                    </div>
                  `
                : nothing}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
